import React, { useRef, useState } from 'react';
import { PiUploadSimple } from 'react-icons/pi';
import type { BaseProps } from 'src/modules/shared/types/component.type';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { fileInputContainerClasses, fileInputButtonSizes } from './styles';
import { FileDownloadButton } from 'src/modules/shared/components/atoms/buttons/file-download-button';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useAlert } from 'src/contexts/alert-context';
import { MAX_FILE_SIZE } from 'src/modules/shared/components/atoms/inputs/file-input/const';
import { ALLOWED_FILE_TYPES } from 'src/modules/shared/components/atoms/inputs/file-input/const';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

/** File details a file field stores as JSON in its value. */
interface StoredFileDetails {
  /** Stored file ID, once the file is uploaded. */
  fileId?: string;
  /** Original filename. */
  filename: string;
  /** Size in bytes. */
  size: number;
}

/**
 * Reads the file details stored as JSON in a file field's value.
 *
 * @param value - Field value, possibly empty or not JSON.
 * @returns The details, or null when the value holds no named, non-empty file.
 */
const readStoredFileDetails = (value?: string): StoredFileDetails | null => {
  if (!value) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;

  const filename = 'filename' in parsed ? parsed.filename : undefined;
  const size = 'size' in parsed ? parsed.size : undefined;
  const fileId = 'fileId' in parsed ? parsed.fileId : undefined;
  if (typeof filename !== 'string' || !filename) return null;
  if (typeof size !== 'number' || !size) return null;

  return {
    filename,
    size,
    fileId: typeof fileId === 'string' ? fileId : undefined,
  };
};

export interface FileInputProps
  extends BaseProps<
    Omit<
      React.ComponentProps<'input'>,
      'value' | 'onChange' | 'type' | 'size' | 'variant'
    >
  > {
  value?: string;
  onChange?: (file: File) => void;
  placeholder?: string;
  containerClassName?: string;
}

export const FileInput: React.FC<FileInputProps> = ({
  value,
  onChange,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  containerClassName = '',
  placeholder,
  ...rest
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showAlert } = useAlert();
  const { t } = useI18n();
  const [selectedFilename, setSelectedFilename] = useState<string | null>(null);

  const handleClick = () => {
    if (!disabled && !loading) {
      fileInputRef.current?.click();
    }
  };

  const validateFile = (file: File): { isValid: boolean; error?: string } => {
    if (!Object.keys(ALLOWED_FILE_TYPES).includes(file.type)) {
      return {
        isValid: false,
        error: t('shared.fileInput.errors.type'),
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        isValid: false,
        error: t('shared.fileInput.errors.size', {
          size: MAX_FILE_SIZE / (1024 * 1024),
        }),
      };
    }

    return { isValid: true };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file: File | undefined = e.target.files?.[0];
    if (!file || !onChange) return;

    const validation = validateFile(file);

    if (!validation.isValid) {
      showAlert(
        validation.error ?? t('shared.fileInput.errors.generic'),
        'error'
      );
      e.target.value = '';
      return;
    }

    setSelectedFilename(file.name);
    onChange(file);
    e.target.value = '';
  };

  const {
    fileId,
    filename,
    size: fileSize,
  } = readStoredFileDetails(value) ?? {};
  const displayFilename = selectedFilename ?? filename ?? null;

  const containerClasses = [
    ...fileInputContainerClasses,
    containerClassName,
  ].join(' ');

  const buttonSize = fileInputButtonSizes[size];

  return (
    <div className={containerClasses}>
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
        disabled={disabled || loading}
        accept={Object.keys(ALLOWED_FILE_TYPES).join(',')}
        {...rest}
      />

      <div className="flex items-center justify-between w-full gap-2 flex-wrap">
        <div className="min-w-0 flex-1 flex items-center justify-end">
          {displayFilename ? (
            <Text
              size="tiny"
              color="secondary"
              className="truncate leading-none whitespace-nowrap"
              title={displayFilename}
            >
              {displayFilename}
            </Text>
          ) : (
            <Text
              size="tiny"
              color="secondary"
              className="truncate leading-none whitespace-nowrap"
            >
              {placeholder ?? ''}
            </Text>
          )}
        </div>

        {fileId && (
          <FileDownloadButton
            fileId={fileId}
            size={buttonSize}
            variant={variant}
            appearance="soft"
            filename={filename}
            fileSize={fileSize}
          />
        )}

        <Tooltip
          content={
            fileId
              ? t('shared.fileInput.replaceHint')
              : t('shared.fileInput.uploadHint')
          }
          placement="top"
          size="sm"
        >
          <Button
            size={buttonSize}
            variant={variant}
            disabled={disabled || loading}
            loading={loading}
            onClick={handleClick}
            className="shrink-0"
          >
            <PiUploadSimple className="w-4 h-4" />
            {fileId
              ? t('shared.fileInput.replace')
              : t('shared.fileInput.upload')}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
